import axiosInstance from "../../../shared/api/axiosInstance";
import { readPaymentReportHeaders } from "./payment-report-headers";
import { configReq } from "../../../shared/lib/functions";

const endpoint = "admin/v2/PaymentOrders";
const config = (token, signal) => ({ ...configReq(token), signal, timeout: 15000 });
const field = (value, key) => value?.[key] ?? value?.[key[0].toUpperCase() + key.slice(1)];
const pendingRefund = (value) => {
  const id = field(value, "pendingRefundId") ?? null;
  if (id !== null && (typeof id !== "string" || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id))) throw new Error("invalid pending refund reference");
  return id;
};
const body = (response) => response.data?.data && !Array.isArray(response.data.data) ? response.data.data : response.data;

export function readConfiguration(value) {
  const result = {};
  for (const key of ["enabled", "testMode", "testKeyConfigured", "liveKeyConfigured"]) {
    result[key] = field(value, key);
    if (typeof result[key] !== "boolean") throw new Error("invalid configuration response");
  }
  for (const key of ["notificationUrl", "redirectUrl", "cancelUrl"]) {
    result[key] = field(value, key);
    if (typeof result[key] !== "string") throw new Error("invalid configuration response");
  }
  result.version = field(value, "version");
  if (!Number.isSafeInteger(result.version) || result.version < 0) throw new Error("invalid configuration version");
  return result;
}

export async function getGatewaySettings(token, signal) {
  return readConfiguration(body(await axiosInstance.get(`${endpoint}/configuration`, config(token, signal))));
}
export async function revealGatewayKey(token, environment) {
  if (!["test", "live"].includes(environment)) throw new Error("invalid key environment");
  const response = await axiosInstance.post(`${endpoint}/configuration/keys/${environment}`, {}, config(token));
  const key = field(response.data, "apiKey");
  if (typeof key !== "string" || key.length > 512) throw new Error("invalid key response");
  return key;
}
export async function saveGatewaySettings(token, value) {
  const payload = { version: value.version, enabled: value.enabled, testMode: value.testMode,
    notificationUrl: value.notificationUrl.trim(), redirectUrl: value.redirectUrl.trim(), cancelUrl: value.cancelUrl.trim(),
    clearTestKey: Boolean(value.clearTestKey), clearLiveKey: Boolean(value.clearLiveKey) };
  if (value.testApiKey?.trim()) payload.testApiKey = value.testApiKey.trim();
  if (value.liveApiKey?.trim()) payload.liveApiKey = value.liveApiKey.trim();
  return readConfiguration(body(await axiosInstance.put(`${endpoint}/configuration`, payload, config(token))));
}

export function reportQuery(filters) {
  const query = new URLSearchParams({ page: String(filters.page || 1), limit: String(filters.limit || 25) });
  if (filters.search?.trim()) query.set("search", filters.search.trim());
  if (filters.status) query.set("status", filters.status);
  if (["zarinpal", "multisafepay"].includes(filters.gatewayCode)) query.set("gatewayCode", filters.gatewayCode);
  if (filters.mode === "test" || filters.mode === "live") query.set("testMode", String(filters.mode === "test"));
  if (filters.from) query.set("from", `${filters.from}T00:00:00Z`);
  if (filters.to) {
    const next = new Date(`${filters.to}T00:00:00Z`); next.setUTCDate(next.getUTCDate() + 1);
    query.set("to", next.toISOString());
  }
  return query.toString();
}

export function readReport(value) {
  const data = field(value, "data"), total = field(value, "total");
  if (!Array.isArray(data) || !Number.isSafeInteger(total) || total < 0) throw new Error("invalid payment report");
  const rows = data.map((row) => {
    const result = {};
    for (const key of ["id", "currency", "status", "businessReference", "description", "createdAt", "updatedAt"]) {
      result[key] = field(row, key);
      if (typeof result[key] !== "string") throw new Error("invalid report row");
    }
    for (const key of ["customerUserId", "amountMinor", "refundedAmountMinor"]) {
      result[key] = field(row, key);
      if (!Number.isSafeInteger(result[key]) || result[key] < 0) throw new Error("invalid report amount");
    }
    result.testMode = field(row, "testMode");
    if (typeof result.testMode !== "boolean") throw new Error("invalid report environment");
    result.providerTransactionId = field(row, "providerTransactionId") || "";
    if (typeof result.providerTransactionId !== "string") throw new Error("invalid provider reference");
    result.pendingRefundId = pendingRefund(row);
    result.gatewayCode = field(row, "gatewayCode") ?? "multisafepay";
    result.clientIpAddress = field(row, "clientIpAddress") ?? null;
    result.customerName = field(row, "customerName") ?? null;
    if (result.customerName !== null && (typeof result.customerName !== "string" || result.customerName.length > 500)) throw new Error("invalid customer name");
    if (result.clientIpAddress !== null && (typeof result.clientIpAddress !== "string" || result.clientIpAddress.length > 45)) throw new Error("invalid client IP");
    if (!["multisafepay", "zarinpal"].includes(result.gatewayCode)) throw new Error("invalid gateway identity");
    return result;
  });
  return { rows, total, headers: readPaymentReportHeaders(field(value, "header")), extraHeaders: readPaymentReportHeaders(field(value, "header"), true) };
}

export async function getPaymentReport(token, filters, signal) {
  return readReport((await axiosInstance.get(`${endpoint}?${reportQuery(filters)}`, config(token, signal))).data);
}
export async function exportPaymentReport(token, filters) {
  return (await axiosInstance.get(`${endpoint}/export?${reportQuery(filters)}`, { ...config(token), responseType: "blob" })).data;
}
export async function refreshPayment(token, id) {
  const response = await axiosInstance.post(`${endpoint}/${encodeURIComponent(id)}/refresh`, {}, config(token));
  const status = field(response.data, "status");
  if (typeof status !== "string" || !status) throw new Error("invalid payment refresh response");
  return { status, awaitingProvider: field(response.data, "awaitingProvider") === true, pendingRefundId: pendingRefund(response.data) };
}
export function paymentError(error) {
  if (error?.message === "payment-report-headers") return "ساختار ستون‌های گزارش پرداخت دریافت نشد یا معتبر نیست؛ تنظیمات Header و مهاجرت هدر گزارش را بررسی کنید.";
  const status = error?.response?.status;
  if (status === 404) return "API جدید پرداخت در محیط فعلی در دسترس نیست؛ نسخهٔ بک‌اند و مهاجرت‌های پرداخت باید نصب شوند.";
  if (status === 401 || status === 403) return "برای این عملیات دسترسی مدیر لازم است؛ وضعیت ورود و مجوز خود را بررسی کنید.";
  if (status === 409) return "اطلاعات تغییر کرده است؛ دوباره بارگذاری کنید و تغییرات را بررسی کنید.";
  if (status === 400) return "مقادیر درخواست معتبر نیستند؛ ورودی‌ها و محدودهٔ فیلتر را بررسی کنید.";
  return "عملیات انجام نشد؛ اتصال، تنظیمات سرور و مهاجرت‌های پرداخت را بررسی و دوباره تلاش کنید.";
}
